import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@learnwithai/ui-shared';
import { parentApi } from '../api';

export default function ForgotPasswordPage() {
  useTranslation();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  const resetEmail = searchParams.get('email') ?? '';

  if (resetToken) return <ResetPasswordForm token={resetToken} email={resetEmail} />;
  return <ForgotPasswordForm />;
}

function ForgotPasswordForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await parentApi.forgotPassword(email);
      setSent(true);
    } catch (err: unknown) {
      // Always show success to prevent email enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>📧 {t('auth.checkEmail', 'Check Your Email')}</h1>
          <p style={{ textAlign: 'center', color: '#6E6E73' }}>
            {t('auth.resetEmailSent', 'If an account exists with that email, we\'ve sent password reset instructions.')}
          </p>
          <p style={{ textAlign: 'center', marginTop: '24px' }}>
            <a href="/login" style={{ color: '#007AFF' }}>{t('auth.backToLogin', 'Back to Login')}</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>🔑 {t('auth.forgotPassword', 'Forgot Password')}</h1>
        <p style={{ textAlign: 'center', color: '#6E6E73', marginBottom: '24px' }}>
          {t('auth.forgotPasswordInstructions', 'Enter your email and we\'ll send you a link to reset your password.')}
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>
            {t('auth.email')}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={styles.input}
              autoComplete="email"
              autoFocus
            />
          </label>

          {error && <p style={styles.error}>{error}</p>}

          <Button variant="primary" size="lg">
            {loading ? t('common.loading') : t('auth.sendResetLink', 'Send Reset Link')}
          </Button>
        </form>

        <p style={styles.link}>
          <a href="/login" style={{ color: '#007AFF' }}>{t('auth.backToLogin', 'Back to Login')}</a>
        </p>
      </div>
    </div>
  );
}

function ResetPasswordForm({ token, email }: { token: string; email: string }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 12) {
      setError(t('auth.passwordTooShort', 'Password must be at least 12 characters'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.passwordMismatch', 'Passwords do not match'));
      return;
    }

    setLoading(true);
    try {
      await parentApi.resetPassword(email, token, password);
      setDone(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>✅ {t('auth.passwordReset', 'Password Reset')}</h1>
          <p style={{ textAlign: 'center', color: '#6E6E73' }}>
            {t('auth.passwordResetSuccess', 'Your password has been updated. You can now log in.')}
          </p>
          <p style={{ textAlign: 'center', marginTop: '24px' }}>
            <a href="/login" style={{ color: '#007AFF' }}>{t('auth.backToLogin', 'Back to Login')}</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>🔑 {t('auth.resetPassword', 'Reset Password')}</h1>

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>
            {t('auth.newPassword', 'New Password')}
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={12}
              style={styles.input}
              autoComplete="new-password"
              autoFocus
            />
          </label>

          <label style={styles.label}>
            {t('auth.confirmPassword', 'Confirm Password')}
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              style={styles.input}
              autoComplete="new-password"
            />
          </label>

          {error && <p style={styles.error}>{error}</p>}

          <Button variant="primary" size="lg">
            {loading ? t('common.loading') : t('auth.resetPassword', 'Reset Password')}
          </Button>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', padding: '24px', flexDirection: 'column' as const },
  card: { background: '#fff', borderRadius: '16px', padding: '40px', width: '100%', maxWidth: '420px', boxShadow: '0 2px 16px rgba(0,0,0,0.08)' },
  title: { textAlign: 'center', color: '#007AFF', marginBottom: '4px' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  label: { display: 'flex', flexDirection: 'column', gap: '4px', fontWeight: 500 },
  input: { padding: '12px', borderRadius: '8px', border: '1px solid #D1D1D6', fontSize: '1rem' },
  error: { color: '#FF3B30', fontSize: '0.9rem' },
  link: { textAlign: 'center', marginTop: '16px', color: '#6E6E73' },
};
